import { useState, useEffect, useCallback, useRef } from "react";
import { Search, X, Clock, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { barsService, usersService, eventsService } from "@/services";

type SearchScope = "all" | "bars" | "drinks" | "users" | "events";

interface SearchResult {
  id: string;
  type: "bar" | "drink" | "user" | "event";
  title: string;
  subtitle?: string;
  url: string;
}

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<SearchScope>("all");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("admin_recent_searches");
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery("");
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Search function — nutzt searchBars/searchUsers statt In-Memory-Filter
  const performSearch = useCallback(
    async (searchQuery: string, searchScope: SearchScope) => {
      if (!searchQuery.trim()) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const searches: Promise<SearchResult[]>[] = [];

        if (searchScope === "all" || searchScope === "bars") {
          searches.push(
            barsService.searchBars(searchQuery, 5).then((bars) =>
              bars.map((bar) => ({
                id: bar.id,
                type: "bar" as const,
                title: bar.name,
                subtitle: `${bar.city || "—"} · ${bar.isVerified ? "Verifiziert" : "Nicht verifiziert"}`,
                url: `/dashboard/bars/${bar.id}`,
              })),
            ),
          );
        }

        if (searchScope === "all" || searchScope === "users") {
          searches.push(
            usersService.searchUsers(searchQuery, 5).then((users) =>
              users.map((user) => ({
                id: user.id,
                type: "user" as const,
                title: user.profile?.displayName || user.email,
                subtitle: user.email,
                url: `/dashboard/users?id=${user.id}`,
              })),
            ),
          );
        }

        if (searchScope === "all" || searchScope === "events") {
          const lowerQuery = searchQuery.toLowerCase();
          searches.push(
            eventsService.getAllEvents().then((events) =>
              events
                .filter(
                  (e) =>
                    e.title.toLowerCase().includes(lowerQuery) ||
                    e.description?.toLowerCase().includes(lowerQuery),
                )
                .slice(0, 5)
                .map((event) => ({
                  id: event.id,
                  type: "event" as const,
                  title: event.title,
                  subtitle: `${event.type} · ${event.barName || event.barId}`,
                  url: `/dashboard/events?id=${event.id}`,
                })),
            ),
          );
        }

        const resultGroups = await Promise.all(searches);
        setResults(resultGroups.flat());
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query, scope);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, scope, performSearch]);

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      // Save to recent searches
      const updated = [
        query,
        ...recentSearches.filter((s) => s !== query),
      ].slice(0, 5);
      setRecentSearches(updated);
      localStorage.setItem("admin_recent_searches", JSON.stringify(updated));

      // Navigate
      navigate(result.url);
      onClose();
    },
    [query, recentSearches, navigate, onClose]
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          onClose();
          break;
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (results[selectedIndex]) {
            handleResultClick(results[selectedIndex]);
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose, handleResultClick]);

  const handleRecentSearchClick = (search: string) => {
    setQuery(search);
    inputRef.current?.focus();
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem("admin_recent_searches");
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Search Modal */}
      <div className="fixed left-1/2 top-20 z-50 w-full max-w-2xl -translate-x-1/2 transform">
        <div className="mx-4 rounded-lg bg-white shadow-2xl">
          {/* Search Input */}
          <div className="flex items-center gap-3 border-b border-gray-200 p-4">
            <Search className="h-5 w-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search bars, drinks, users, events..."
              className="flex-1 text-lg outline-none placeholder:text-gray-400"
            />
            <button
              onClick={onClose}
              className="rounded-lg p-1 hover:bg-gray-100"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* Scope Tabs */}
          <div className="flex gap-2 border-b border-gray-200 px-4 py-2 overflow-x-auto">
            {[
              { value: "all" as const, label: "All" },
              { value: "bars" as const, label: "Bars" },
              { value: "drinks" as const, label: "Drinks" },
              { value: "users" as const, label: "Users" },
              { value: "events" as const, label: "Events" },
            ].map((tab) => (
              <button
                key={tab.value}
                onClick={() => setScope(tab.value)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                  scope === tab.value
                    ? "bg-primary-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Results / Recent Searches */}
          <div className="max-h-96 overflow-y-auto p-2">
            {loading ? (
              <div className="p-8 text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent"></div>
                <p className="mt-2 text-sm text-gray-600">Searching...</p>
              </div>
            ) : query.trim() ? (
              results.length > 0 ? (
                results.map((result, index) => (
                  <button
                    key={result.id}
                    onClick={() => handleResultClick(result)}
                    className={`flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors ${
                      index === selectedIndex
                        ? "bg-primary-50"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {result.title}
                      </p>
                      {result.subtitle && (
                        <p className="text-sm text-gray-600">
                          {result.subtitle}
                        </p>
                      )}
                    </div>
                    <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                      {result.type}
                    </span>
                  </button>
                ))
              ) : (
                <div className="p-8 text-center">
                  <p className="text-gray-600">No results found</p>
                </div>
              )
            ) : recentSearches.length > 0 ? (
              <div>
                <div className="flex items-center justify-between px-3 py-2">
                  <p className="text-sm font-medium text-gray-700">
                    Recent Searches
                  </p>
                  <button
                    onClick={clearRecentSearches}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Clear
                  </button>
                </div>
                {recentSearches.map((search, index) => (
                  <button
                    key={index}
                    onClick={() => handleRecentSearchClick(search)}
                    className="flex w-full items-center gap-3 rounded-lg p-3 text-left hover:bg-gray-50"
                  >
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900">{search}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <p className="text-sm text-gray-600">
                  Start typing to search...
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex gap-4">
                <span>↑↓ Navigate</span>
                <span>↵ Select</span>
                <span>Esc Close</span>
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                <span>Cmd+K to open</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
